<script setup lang="ts">
// PRIMER-VUE &
import { default as PButton } from 'primevue/button'
import { default as PMessage } from 'primevue/message'
import { default as draggable } from 'vuedraggable'

import CQuizQuestionListItem from '@/presentation/components/quiz/CQuizQuestionListItem.vue'
import type { ModelRef } from 'vue'
import type { Quiz } from '@/core/domain/quiz/Quiz.ts'
import type { QuizQuestion } from '@/core/domain/quiz/QuizQuestion.ts'
import { createQuizQuestionOfType } from '@/core/domain/quiz/QuizQuestion.ts'
import { useConfirm } from 'primevue/useconfirm'
import { useQuizStore } from '@/presentation/stores/quiz/quiz.ts'
import { computed } from 'vue'
import { LearningQuizQuestionType } from '@/core/domain/resources/enums/LearningQuizQuestion.ts'
import type { QuizVersion } from '@/core/domain/quiz/QuizVersion.ts'
import { useQuizAccessUtils } from '@/presentation/composables/quiz/useQuizAccessUtils.ts'

interface DragEndEvent {
  oldIndex: number
  newIndex: number
}

// SERVICES
const confirm = useConfirm()
const quizStore = useQuizStore()
const { canEditQuizVersion } = useQuizAccessUtils()

// MODELS
const questions: ModelRef<QuizQuestion[]> = defineModel('questions', { required: true })
const currentDisplayedQuestionIndex: ModelRef<number> = defineModel(
  'currentDisplayedQuestionIndex',
  {
    required: true
  }
)

const emits = defineEmits(['cancel:update', 'version-change-needed'])

const props = defineProps<{
  quiz: Quiz
  hasUnsavedChanges: boolean
  selectedVersion: QuizVersion | null
}>()

const dragOptions = {
  animation: 150,
  group: 'group1',
  disabled: false,
  ghostClass: 'ghost'
}

// Computed properties pour les contraintes de versioning
const canModifyStructure = computed(() => props.selectedVersion?.canModifyStructure ?? false)
const canModifyContent = computed(() => props.selectedVersion?.canModifyContent ?? false)
const canReorderQuestions = computed(() => props.selectedVersion?.canReorderQuestions ?? false)

function displayQuestion(index: number) {
  console.log(index)
  currentDisplayedQuestionIndex.value = index
}

function startEditing(index: number) {
  if (currentDisplayedQuestionIndex.value === index) {
    return
  }

  if (props.hasUnsavedChanges) {
    confirm.require({
      message: 'Vous avez des modifications non enregistrées. Voulez-vous les enregistrer ?',
      header: 'Confirmation',
      acceptLabel: 'Oui',
      rejectLabel: 'Non',
      acceptIcon: 'pi pi-check',
      rejectIcon: 'pi pi-times',
      accept: () => {
        if (props.selectedVersion) {
          quizStore.updateQuizVersion(props.quiz.id, props.selectedVersion)
        }
        displayQuestion(index)
      },
      reject: () => {
        emits('cancel:update')
        displayQuestion(index)
      }
    })
  } else {
    displayQuestion(index)
  }
}

function handleDropEnd(event: DragEndEvent) {
  if (!canReorderQuestions.value) {
    handleStructuralModificationAttempt('réorganiser les questions')
    return
  }

  try {
    if (currentDisplayedQuestionIndex.value === event.oldIndex)
      currentDisplayedQuestionIndex.value = event.newIndex
    else if (currentDisplayedQuestionIndex.value > event.newIndex) {
      currentDisplayedQuestionIndex.value--
    } else if (currentDisplayedQuestionIndex.value < event.newIndex) {
      currentDisplayedQuestionIndex.value++
    }

    props.selectedVersion?.updateQuestionsOrder()
  } catch (error) {
    console.error('Erreur lors de la réorganisation:', error)
    handleStructuralModificationAttempt('réorganiser les questions')
  }
}

function addNewQuestion() {
  if (!canModifyStructure.value) {
    handleStructuralModificationAttempt('ajouter une question')
    return
  }

  try {
    const createdQuestion = props.selectedVersion?.addNewQuestion()
    if (createdQuestion) {
      // Mettre à jour la ref questions
      questions.value = [...props.selectedVersion!.questions]
      currentDisplayedQuestionIndex.value = createdQuestion.order - 1
    }
  } catch (error) {
    console.error("Erreur lors de l'ajout de question:", error)
    handleStructuralModificationAttempt('ajouter une question')
  }
}

function duplicateQuestion(questionToDuplicate: QuizQuestion) {
  if (!canModifyStructure.value) {
    handleStructuralModificationAttempt('dupliquer une question')
    return
  }

  try {
    const duplicatedQuestion = props.selectedVersion?.duplicateQuestion(questionToDuplicate)
    if (duplicatedQuestion) {
      // Mettre à jour la ref questions
      questions.value = [...props.selectedVersion!.questions]
      currentDisplayedQuestionIndex.value = duplicatedQuestion.order - 1
    }
  } catch (error) {
    console.error('Erreur lors de la duplication:', error)
    handleStructuralModificationAttempt('dupliquer une question')
  }
}

function deleteQuestion(index: number) {
  if (!canModifyStructure.value) {
    handleStructuralModificationAttempt('supprimer une question')
    return
  }

  confirm.require({
    message: 'Êtes-vous sûr de vouloir supprimer cette question ?',
    header: 'Confirmation de suppression',
    acceptLabel: 'Oui, supprimer',
    rejectLabel: 'Annuler',
    acceptIcon: 'pi pi-check',
    rejectIcon: 'pi pi-times',
    accept: () => {
      try {
        props.selectedVersion?.removeQuestion(index)
        // Mettre à jour la ref questions
        questions.value = [...props.selectedVersion!.questions]

        if (currentDisplayedQuestionIndex.value === index) {
          currentDisplayedQuestionIndex.value = questions.value.length > 0 ? 0 : -1
        } else if (currentDisplayedQuestionIndex.value > index) {
          currentDisplayedQuestionIndex.value--
        }
      } catch (error) {
        console.error('Erreur lors de la suppression:', error)
        handleStructuralModificationAttempt('supprimer une question')
      }
    },
    reject: () => {}
  })
}

function handleStructuralModificationAttempt(action: string) {
  const message =
    action === 'réorganiser les questions'
      ? `Pour ${action}, vous devez créer une nouvelle version brouillon. La version archivée ne peut être modifiée.`
      : `Pour ${action}, vous devez créer une nouvelle version brouillon. La version active ne peut être modifiée que pour les libellés, feedbacks et réorganisation des questions.`

  confirm.require({
    message,
    header: 'Modification non autorisée',
    acceptLabel: 'Créer une nouvelle version',
    rejectLabel: 'Annuler',
    acceptIcon: 'pi pi-plus',
    rejectIcon: 'pi pi-times',
    accept: () => {
      // Proposer de créer automatiquement une nouvelle version
      createDraftVersion()
    },
    reject: () => {}
  })
}

function handleQuestionTypeChange(changeData: {
  index: number
  oldType: LearningQuizQuestionType.Values
  newType: LearningQuizQuestionType.Values
  question: QuizQuestion
}) {
  if (!canModifyStructure.value) {
    handleStructuralModificationAttempt('changer le type de question')
    return
  }

  try {
    // Créer une nouvelle instance de question avec le nouveau type
    const newQuestion = createQuizQuestionOfType(changeData.question, changeData.newType)
    console.log(newQuestion)
    // Mettre à jour la question dans la liste
    questions.value[changeData.index] = newQuestion
  } catch (error) {
    console.error('Erreur lors du changement de type:', error)
    handleStructuralModificationAttempt('changer le type de question')
  }
}

async function createDraftVersion() {
  try {
    const newVersion = await quizStore.createQuizVersion(props.quiz.id, {
      comment: 'Version créée automatiquement pour permettre les modifications',
      fromVersionId: props.selectedVersion?.id
    })

    if (newVersion) {
      emits('version-change-needed', newVersion)
    }
  } catch (error) {
    console.error('Erreur lors de la création de la nouvelle version:', error)
  }
}
</script>

<template>
  <div
    class="bg-surface-50 sticky top-0 z-10 grid h-fit w-full grid-cols-12 justify-between gap-4 pb-4"
  >
    <h4 class="col-span-6 content-center text-lg font-bold">Liste des questions</h4>

    <p-button
      label="Ajouter une question"
      icon="pi pi-plus"
      outlined
      class="col-span-6"
      size="small"
      @click="addNewQuestion()"
      :disabled="!canModifyContent || !canEditQuizVersion(props.selectedVersion)"
    />
  </div>

  <!-- Message d'information sur les contraintes de version -->
  <p-message
    v-if="props.selectedVersion && !canModifyStructure && canModifyContent"
    severity="warn"
    :closable="true"
    class="mt-4"
  >
    Version active : seuls les libellés, feedbacks et réorganisation des questions peuvent être
    modifiés.
  </p-message>

  <p-message
    v-if="props.selectedVersion && !canModifyContent"
    severity="warn"
    :closable="true"
    class="mt-4"
  >
    Version archivée : aucune modification autorisée.
  </p-message>

  <div class="mt-6 flex w-full flex-col items-start">
    <template v-if="questions.length > 0">
      <draggable
        class="list-group flex w-full flex-col gap-4"
        item-key="order"
        handle=".handle"
        :component-data="{ tag: 'div', name: 'flip-list', type: 'transition' }"
        v-model="questions"
        v-bind="dragOptions"
        v-if="questions.length > 0"
        @end="handleDropEnd"
        :disabled="!canReorderQuestions || !canEditQuizVersion(props.selectedVersion)"
      >
        <template #item="{ element, index }: { element: QuizQuestion; index: number }">
          <div>
            <c-quiz-question-list-item
              :question="element"
              :isEditing="currentDisplayedQuestionIndex === index"
              :index="index"
              :can-modify-structure="
                canModifyStructure && canEditQuizVersion(props.selectedVersion)
              "
              :can-modify-content="canModifyContent && canEditQuizVersion(props.selectedVersion)"
              @click="startEditing(index)"
              @edit="startEditing(index)"
              @duplicate="
                (questionToDuplicate: QuizQuestion) => duplicateQuestion(questionToDuplicate)
              "
              @delete="() => deleteQuestion(index)"
              @type-change="handleQuestionTypeChange"
            />
          </div>
        </template>
      </draggable>
      <p-button
        label="Ajouter une question"
        icon="pi pi-plus"
        outlined
        class="col-span-6 mt-4"
        size="small"
        @click="addNewQuestion()"
        :disabled="!canModifyContent || !canEditQuizVersion(props.selectedVersion)"
      />
    </template>
    <div v-else class="w-full">
      <p-message severity="info" icon="pi pi-info-circle">
        Il n'y a pas de questions pour ce quiz.
        <span v-if="canModifyContent">Cliquez sur "Ajouter une question" pour en créer une.</span>
        <span v-else>Impossible d'ajouter des questions à cette version.</span>
      </p-message>
    </div>
  </div>
</template>
